/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from "react";
import { LoadingState } from "underwriter_dashboard/shared/types";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import { InlineNotification, Form, FormGroup, Loading, Modal } from "carbon-components-react";
import Field from "./Field";

import ImportData from "underwriter_dashboard/shared/WorksheetEditor/ImportData";
import { fetchWorksheets } from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";
import { WorksheetTableProps } from "./WorksheetTableProps";
import { WorksheetName } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import {
  createWorksheetIfNeeded,
  deleteWorksheet,
  saveWorksheet,
  updateWorksheet,
} from "../helpers";
import { WorksheetAs } from "@eave/shared/types/api/worksheets";

const WorksheetTable: React.FC<WorksheetTableProps> = ({
  app,
  headerData,
  loadApp,
  modalFields = [],
  title,
  enableOverflowFeeDuplication = false,
  type = WorksheetAs.Table,
  wsName,
  wsRecordName,
  wsSliceName,
  worksheetImportDataFn,
  worksheetToRow,
  worksheetToFormState,
}: WorksheetTableProps) => {
  // The top-level worksheet (e.g. Prorations)
  const [loadedWorksheet, setLoadedWorksheet] = useState(null);

  // Either the top-level ws, or the slice we want to work with (e.g. the "PaidBySeller" slice on Prorations)
  const [worksheets, setWorksheets] = useState(null);

  useEffect(() => {
    loadWorksheet(wsName);
  }, []);

  useEffect(() => {
    // If we want to load a specific slice from a worksheet
    if (wsSliceName) {
      setWorksheets(loadedWorksheet?.[wsSliceName]);
    } else {
      setWorksheets(loadedWorksheet);
    }
  }, [loadedWorksheet]);

  const [formState, setFormState] = useState(null as any);
  const [formUpdates, setFormUpdates] = useState(null as any);

  // UI
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [editingMode, setEditingMode] = useState(EditingState.CREATE);
  const [invalidFields, setInvalidFields] = useState({});
  const [loadingState, setLoadingState] = useState(LoadingState.INACTIVE);
  const [errorMessage, setErrorMessage] = useState("");

  // Optionally transform the row data, header data, and modal form fields data
  const rowData = type === WorksheetAs.Object ? [worksheets] : worksheets;
  const rowDataTransformed = worksheetToRow ? rowData?.map(worksheetToRow) : rowData;
  const modalFormFields = typeof modalFields === "function" ? modalFields(formState) : modalFields;
  const headers =
    worksheets === null
      ? []
      : typeof headerData === "function"
      ? headerData(worksheets)
      : headerData;

  const validateForm = (name, form, validation) => {
    if (!validation) {
      return;
    }

    const copy = invalidFields;

    // clear this field's prior run validation
    delete copy[name];

    // and reset with new msg, if invalid
    const value = form[name];
    const invalidMsg = validation(value);
    if (invalidMsg !== "") {
      copy[name] = invalidMsg;
    }

    setInvalidFields(copy);
  };

  const handleChange = (name, value) => {
    // clear error msg
    setErrorMessage("");

    // just the updates, for persistence.
    setFormUpdates({ ...formUpdates, [name]: value });

    const updatedForm = { ...formState, [name]: value };

    // the entire form state, with updates, for display in the modal.
    setFormState(updatedForm);

    // validate after update, since input fields will display even invalid results.
    const fieldDef = modalFormFields.find(field => field.name === name);
    validateForm(name, updatedForm, fieldDef.validation);
  };

  /**
   * Loads the specified worksheet and updates the state
   * @param wsName The name of the worksheet to load.
   * @returns Promise that sets the worksheet to local state
   */
  const loadWorksheet = (wsName: WorksheetName) => {
    setLoadingState(LoadingState.ACTIVE);

    return fetchWorksheets(app).then(worksheets => {
      createWorksheetIfNeeded(app, worksheets, wsName).then(worksheets => {
        try {
          worksheetToFormState(app, worksheets, setLoadedWorksheet);
          setLoadingState(LoadingState.FINISHED);
        } catch (err) {
          setErrorMessage(err);
          setLoadingState(LoadingState.FINISHED);
        }
      });
    });
  };

  return (
    <>
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestClose={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          setLoadingState(LoadingState.ACTIVE);

          deleteWorksheet(wsSliceName, loadedWorksheet, formState)
            .then(() => {
              loadApp(app.id);
            })
            .catch(err => {
              setErrorMessage(err.meta.error_detail);
              setLoadingState(LoadingState.FINISHED);
            });
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}
      </Modal>

      <Modal
        open={modalIsOpen}
        modalHeading={editingMode === EditingState.CREATE ? `Add ${wsName}` : `Update ${wsName}`}
        primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={Object.keys(invalidFields).length > 0}
        onRequestClose={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onBlur={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          // check all form fields, to confirm all are valid.
          const currentInvalidFields = modalFormFields.reduce((acc, fieldDef) => {
            if (!fieldDef.validation) {
              return acc;
            }
            const value = formState[fieldDef.name];
            const invalidMsg = fieldDef.validation(value);
            if (invalidMsg === "") {
              return acc;
            }
            return {
              ...acc,
              [fieldDef.name]: invalidMsg,
            };
          }, {});

          setInvalidFields(currentInvalidFields);

          if (Object.keys(currentInvalidFields).length > 0) {
            setErrorMessage("There are invalid fields. Please check again.");
            return;
          }

          if (editingMode === EditingState.CREATE) {
            setLoadingState(LoadingState.ACTIVE);

            saveWorksheet(
              app,
              loadedWorksheet,
              wsRecordName,
              wsSliceName,
              formUpdates,
              modalFormFields,
            )
              .then(() => {
                loadWorksheet(wsName);
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          } else if (editingMode === EditingState.UPDATE) {
            setLoadingState(LoadingState.ACTIVE);

            const { id, version } = formState;

            updateWorksheet(app, id, version, formUpdates, modalFormFields)
              .then(() => {
                loadWorksheet(wsName);
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          }
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}
        {formState && modalIsOpen && worksheets && (
          <>
            <Form onSubmit={() => {}}>
              <FormGroup legendText="">
                {modalFormFields.map(fieldDef => (
                  <div key={`${fieldDef.name}-wrapper`}>
                    <Field
                      fieldDef={fieldDef}
                      value={formState[fieldDef.name]}
                      invalidMsg={invalidFields[fieldDef.name]}
                      handleChange={handleChange}
                    />
                    <div key={`${fieldDef.name}-margin-bottom`} style={{ height: "10px" }} />
                  </div>
                ))}
                <div style={{ height: "20px" }} />
              </FormGroup>
            </Form>
          </>
        )}
      </Modal>

      {worksheetImportDataFn && (
        <ImportData
          wsName={wsName}
          onClick={() => {
            worksheetImportDataFn(wsName, worksheets.id);
          }}
        />
      )}
      {worksheets && (
        <BaseTable
          title={title}
          headerData={headers}
          enableOverflowFeeDuplication={enableOverflowFeeDuplication}
          rawRowData={rowDataTransformed}
          rowData={rowDataTransformed}
          collapseOverride={worksheets?.length === 0}
          openModal={() => setModalIsOpen(true)}
          openDeleteModal={() => setDeleteModalIsOpen(true)}
          updateEditingMode={setEditingMode}
          initializeFormState={() => setFormState({} as any)}
          updateFormState={data => setFormState(data)}
          updateFormUpdates={data => setFormUpdates(data)}
          emptyRowText="No worksheet records to show."
          disableBatch
          disableAddNew={type === WorksheetAs.Object} // If the worksheet is non-tabular, we can't add new records
        />
      )}
    </>
  );
};

export default WorksheetTable;
